import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';

import {
  Button,
  Combobox,
  Icon,
  Input,
  Modal,
  Radio,
  RadioButtonGroup,
  Tooltip
} from '@salesforce/design-system-react';
import { sortByField, trimAndStrip } from '../../../utilities/stringUtils';

import './WeatherAttributeEditModal.scss';
import escapeRegExp from 'lodash.escaperegexp';

class WeatherAttributeEditModal extends Component {
  constructor(props) {
    super(props);
    this.modalName = 'weatherAttributeEdit';
    this.optionFor = this.optionFor.bind(this);
    this.anchors = {
      addLink: React.createRef()
    };

    this.state = {
      valueHasBlurred: false,
      inputValue: ''
    };

    this.fields = [];
    this.helpLink =
      'https://help.salesforce.com/s/articleView?id=sf.mc_pb_einstein_use_weather.htm&type=5';
  }

  weatherRadioButton(weatherType) {
    const { formatMessage } = this.props.intl;

    return (
      <Radio
        key={`${weatherType}`}
        id={`${weatherType}`}
        labels={{
          label: formatMessage({
            id: this.getI18nID(`${weatherType}.title`, false, true)
          })
        }}
        value={`${weatherType}`}
        variant="button-group"
        checked={this.getActiveWeatherType() === weatherType}
      />
    );
  }

  addValueRow() {
    const attribute = { ...this.attribute() };
    attribute.values = [...attribute.values, ''];
    attribute.details.forecast_ranges = [
      ...attribute.details.forecast_ranges,
      {}
    ];

    this.props.actions.updateModal(this.modalName, {
      attribute,
      addedValue: true
    });
  }

  deleteValueRow(idx) {
    const attribute = { ...this.attribute() };
    const values = [...attribute.values];
    const details = attribute.details;

    values.splice(idx, 1);
    details.forecast_ranges.splice(idx, 1);

    this.props.actions.updateModal(this.modalName, {
      attribute: { ...attribute, values, details }
    });
  }

  clearForecastRow() {
    const attribute = {
      ...this.attribute(),
      values: [],
      details: { forecast_ranges: [] }
    };

    this.props.actions.updateModal(this.modalName, { attribute });
  }

  updateForecastRow(idx, element, value) {
    const attribute = {
      ...this.attribute(),
      values: [...this.attribute().values]
    };

    if (
      idx === 0 &&
      attribute.values.length === 1 &&
      attribute.values[idx] === '' &&
      attribute.details?.forecast_ranges.length === 0
    ) {
      if (element === 'value') {
        attribute.values[0] = value;
      }

      const forecastValue = this.updateForecastRange({}, element, value);

      attribute.details.forecast_ranges.shift();
      attribute.details.forecast_ranges.push(forecastValue);
    } else if (!!attribute.details.forecast_ranges[idx]) {
      if (element === 'value') {
        attribute.values[idx] = value;
      }

      attribute.details.forecast_ranges[idx] = this.updateForecastRange(
        attribute.details.forecast_ranges[idx],
        element,
        value
      );
    } else {
      let forecastValue = {};
      if (element === 'value') {
        attribute.values.push(value);
      }

      forecastValue = this.updateForecastRange({}, element, value);
      attribute.details.forecast_ranges.push(forecastValue);
    }

    this.props.actions.updateModal(this.modalName, { attribute });
  }

  updateForecastRange(forecastValue, key, value) {
    const newForecastValue = { ...forecastValue };

    if (!key.startsWith('precip')) {
      newForecastValue[key] = value;
      //remove after forecastType selection is added to UI
      if (
        this.props.modals.weatherAttributeEdit.data.attribute.type ===
        'WeatherTemperatureAttribute'
      ) {
        newForecastValue['forecastType'] = 'highTemp';
      }
    } else if (key === 'precipThan') {
      if (
        forecastValue.hasOwnProperty('greaterThan') ||
        forecastValue.hasOwnProperty('lessThan')
      ) {
        for (const origKey in forecastValue) {
          if (origKey.endsWith('Than')) {
            newForecastValue[value] = newForecastValue[origKey];
            delete newForecastValue[origKey];
          }
        }
      } else {
        newForecastValue[value] = null;
      }
    } else if (key === 'precipPercent') {
      const pctValue = value === '' ? null : parseInt(value);
      if (
        forecastValue.hasOwnProperty('greaterThan') ||
        forecastValue.hasOwnProperty('lessThan')
      ) {
        for (const origKey in forecastValue) {
          if (origKey.endsWith('Than')) {
            newForecastValue[origKey] = pctValue;
          }
        }
      } else {
        // force the issue and choose a comparator for them
        newForecastValue['greaterThan'] = pctValue;
      }
    } else if (key === 'forecastType') {
      newForecastValue[key] = value;
    }
    return newForecastValue;
  }

  resetAttributeProp(type) {
    let inheritanceType, details;

    switch (type) {
      case 'temp':
        inheritanceType = 'WeatherTemperatureAttribute';
        details = { forecast_ranges: [] };
        break;
      case 'precip':
        inheritanceType = 'WeatherPrecipitationAttribute';
        details = { forecast_ranges: [] };
        break;
      case 'alarm':
        inheritanceType = 'WeatherAlarmAttribute';
        details = null;
        break;
      case 'index':
        inheritanceType = 'WeatherIndexAttribute';
        details = null;
        break;
      default:
        inheritanceType = null;
        details = null;
    }

    const attribute = {
      ...this.attribute(),
      type: inheritanceType,
      data_type: null,
      values: [''],
      details: details
    };

    this.props.actions.updateModal(this.modalName, { attribute });
  }

  updateAttributeProp(prop, value) {
    const attribute = { ...this.attribute() };
    attribute[prop] = value;
    this.props.actions.updateModal(this.modalName, { attribute });
  }

  renderTempRow(value, idx) {
    const { formatMessage } = this.props.intl;
    const deleteDisabled = this.values().length <= 1;
    const degreeSetting = this.props.rules?.settings?.weather_degree || 'F';
    const degreeLabel = formatMessage({
      id: this.getI18nID(`temp.degreeSetting.${degreeSetting}`, false, true)
    });

    const attribute = this.attribute();
    const rowDetails = attribute.details.forecast_ranges[idx];

    return (
      <li className="weather-value-row" key={`li-value-${idx}`}>
        <Input
          className="tempName"
          value={attribute.values[idx]}
          onChange={(event, { value }) =>
            this.updateForecastRow(idx, 'value', value)
          }
          placeholder={formatMessage({
            id: this.getI18nID('temp.placeholder', false, true)
          })}
          onBlur={() => this.setState({ valueHasBlurred: true })}
          errorText={
            rowDetails?.value
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        <Input
          className="tempValue"
          type="number"
          variant="base"
          minValue={-999}
          maxValue={999}
          maxLength="3"
          value={
            rowDetails?.greaterThan || rowDetails?.greaterThan === 0
              ? rowDetails.greaterThan
              : ''
          }
          onChange={(event, { value }) =>
            this.updateForecastRow(idx, 'greaterThan', value)
          }
          onBlur={() => this.setState({ valueHasBlurred: true })}
          errorText={
            rowDetails?.greaterThan || rowDetails?.greaterThan === 0
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        <span className="degreeLabel">{degreeLabel}</span>
        <Input
          className="tempValue"
          type="number"
          variant="base"
          minValue={-999}
          maxValue={999}
          maxLength="3"
          value={
            rowDetails?.lessThan || rowDetails?.lessThan === 0
              ? rowDetails.lessThan
              : ''
          }
          onChange={(event, { value }) =>
            this.updateForecastRow(idx, 'lessThan', value)
          }
          onBlur={() => this.setState({ valueHasBlurred: true })}
          errorText={
            rowDetails?.lessThan || rowDetails?.lessThan === 0
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        <span className="degreeLabel">{degreeLabel}</span>
        <Button
          className="delete-value-button"
          iconCategory="utility"
          iconName="delete"
          iconVariant="border-filled"
          variant="icon"
          disabled={deleteDisabled}
          onClick={() => this.deleteValueRow(idx)}
        />
      </li>
    );
  }

  precipForecastTypeOptions() {
    return ['precipitation', 'rain', 'snow', 'ice', 'thunderstorm'].map(
      this.optionFor
    );
  }

  precipThanOptions() {
    return ['greaterThan', 'lessThan'].map(this.optionFor);
  }

  optionFor(precip) {
    const { formatMessage } = this.props.intl;
    return {
      id: precip,
      label: formatMessage({
        id: this.getI18nID(`precip.${precip}`, false, true)
      }),
      value: precip
    };
  }

  currentPrecipType(forecastObj) {
    for (const key in forecastObj) {
      if (key === 'forecastType') {
        return [
          this.precipForecastTypeOptions().find(
            ({ value }) => value === forecastObj.forecastType
          )
        ];
      }
    }

    return null;
  }

  currentPrecipThan(forecastObj) {
    for (const key in forecastObj) {
      if (key === 'greaterThan' || key === 'lessThan') {
        return [this.precipThanOptions().find(({ value }) => value === key)];
      }
    }

    return null;
  }

  currentPrecipPercent(forecastObj) {
    for (const key in forecastObj) {
      if (key === 'greaterThan' || key === 'lessThan') {
        return forecastObj[key];
      }
    }

    return '';
  }

  renderPrecipRow(value, idx) {
    const { formatMessage } = this.props.intl;
    const deleteDisabled = this.values().length <= 1;

    const attribute = this.attribute();
    const rowDetails = attribute.details.forecast_ranges[idx];

    const percent = this.currentPrecipPercent(rowDetails);

    return (
      <li className="weather-value-row" key={`li-value-${idx}`}>
        <Input
          className="precipName"
          value={attribute.values[idx]}
          required
          placeholder={formatMessage({
            id: this.getI18nID('precip.placeholder', false, true)
          })}
          onChange={(event, { value }) =>
            this.updateForecastRow(idx, 'value', value)
          }
          onBlur={() => this.setState({ valueHasBlurred: true })}
          errorText={
            rowDetails?.value
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        <Combobox
          className="forecastType"
          variant="readonly"
          options={this.precipForecastTypeOptions()}
          selection={this.currentPrecipType(rowDetails) || []}
          required
          labels={{ placeholderReadOnly: null }}
          events={{
            onSelect: (event, data) => {
              const { selection } = data;

              if (selection.length === 1) {
                this.updateForecastRow(idx, 'forecastType', selection[0].value);
              }
            }
          }}
          errorText={
            this.currentPrecipType(rowDetails)
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        <Combobox
          className="precipThan"
          variant="readonly"
          options={this.precipThanOptions()}
          selection={this.currentPrecipThan(rowDetails) || []}
          required
          labels={{ placeholderReadOnly: null }}
          events={{
            onSelect: (event, data) => {
              const { selection } = data;

              if (selection.length === 1) {
                this.updateForecastRow(idx, 'precipThan', selection[0].value);
              }
            }
          }}
          errorText={
            this.currentPrecipThan(rowDetails)
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        <Input
          className="precipValue"
          type="number"
          variant="base"
          minValue={0}
          maxValue={100}
          maxLength="3"
          value={this.currentPrecipPercent(rowDetails)}
          required
          onChange={(event, { value }) =>
            this.updateForecastRow(idx, 'precipPercent', value)
          }
          onBlur={() => this.setState({ valueHasBlurred: true })}
          errorText={
            percent || percent === 0
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
        />
        %
        <Button
          className="delete-value-button"
          iconCategory="utility"
          iconName="delete"
          iconVariant="border-filled"
          variant="icon"
          disabled={deleteDisabled}
          onClick={() => this.deleteValueRow(idx)}
        />
      </li>
    );
  }

  noValuesWarning() {
    const attribute = this.attribute();
    const forecast_ranges = attribute.details.forecast_ranges;
    const hasTempRange = forecast_ranges.every(function(range) {
      return (
        range.value &&
        range.forecastType &&
        (range.greaterThan || range.greaterThan === 0) &&
        (range.lessThan || range.lessThan === 0)
      );
    });
    const hasPrecipRange = forecast_ranges.every(function(range) {
      return (
        range.value &&
        range.forecastType &&
        ((range.greaterThan && range.greaterThan >= 0) ||
          (range.lessThan && range.lessThan >= 0))
      );
    });
    if (attribute.type === 'WeatherTemperatureAttribute') {
      return hasTempRange;
    } else if (attribute.type === 'WeatherPrecipitationAttribute') {
      return hasPrecipRange;
    }
  }

  renderRequired() {
    return (
      <abbr className="slds-required" title="required">
        *
      </abbr>
    );
  }

  getActiveWeatherType() {
    switch (this.attribute().type) {
      case 'WeatherTemperatureAttribute':
        return 'temp';
      case 'WeatherPrecipitationAttribute':
        return 'precip';
      case 'WeatherAlarmAttribute':
        return 'alarm';
      case 'WeatherIndexAttribute':
        return 'index';
      default:
        return null;
    }
  }

  renderModal() {
    const { formatMessage } = this.props.intl;
    const weatherTypeLabel = formatMessage({
      id: this.getI18nID('weatherType', false, true)
    });

    const weatherTypeRadioGroup = (
      <RadioButtonGroup
        className="weather-type"
        labels={{ label: weatherTypeLabel }}
        onChange={event => {
          this.clearForecastRow();
          this.resetAttributeProp(event.target.value);
        }}
      >
        {this.weatherRadioButton('temp')}
        {this.weatherRadioButton('precip')}
        {this.weatherRadioButton('alarm')}
        {this.weatherRadioButton('index')}
      </RadioButtonGroup>
    );

    const addNewValuesLink = (
      <span
        className="add-value-link"
        onClick={this.addValueRow.bind(this)}
        ref={this.anchors.addLink}
      >
        <FormattedMessage id={this.getI18nID('addValue')} />
      </span>
    );

    const activeType = this.getActiveWeatherType();
    return (
      <React.Fragment>
        <div className="weather-info-box">
          <Icon
            assistiveText={{ label: 'Info' }}
            className="warning-icon"
            category="utility"
            name="info"
            size="small"
          />
          <span className="warning-label">
            <FormattedMessage
              id={this.getI18nID('help', false, true)}
              values={{
                link: (
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={this.helpLink}
                  >
                    {this.props.intl.formatMessage({
                      id: 'components.ecs.nocatalog.learnMoreText'
                    })}
                  </a>
                )
              }}
            />
          </span>
        </div>
        <div className="weather-modal-content">
          <h2>
            <FormattedMessage
              id={this.getI18nID('detailsHeader', false, true)}
            />
          </h2>
          <div className="attribute-details-row attribute-modal-input">
            <Input
              label={formatMessage({ id: this.getI18nID('name') })}
              value={this.attribute().name || ''}
              required={true}
              disabled={this.mode() === 'edit'}
              onChange={(event, { value }) =>
                this.updateAttributeProp('name', value)
              }
            />
          </div>
          <div className="weather-type-row attribute-modal-input">
            {weatherTypeRadioGroup}
          </div>
          {activeType === 'temp' ? (
            <div>
              <span className="attribute-value-description">
                {this.renderRequired()}
                <FormattedMessage
                  id={this.getI18nID('temp.valuesDescription', false, true)}
                />
              </span>
              <ul className="attribute-value-list">
                {this.values().map(this.renderTempRow.bind(this))}
              </ul>
              {addNewValuesLink}
            </div>
          ) : null}
          {activeType === 'precip' ? (
            <div>
              <span className="attribute-value-description">
                {this.renderRequired()}
                <FormattedMessage
                  id={this.getI18nID('precip.valuesDescription', false, true)}
                />
              </span>
              <ul className="attribute-value-list">
                {this.values().map(this.renderPrecipRow.bind(this))}
              </ul>
              {addNewValuesLink}
            </div>
          ) : null}
          {activeType === 'alarm' ? this.alarmMessage() : null}
          {activeType === 'index' ? this.indicesCombobox() : null}
        </div>
      </React.Fragment>
    );
  }

  renderTooltip(eventType) {
    const { formatMessage } = this.props.intl;

    return (
      <Tooltip
        align="top left"
        content={formatMessage({
          id: this.getI18nID(`${eventType}.tooltip`, false, true)
        })}
        postition="absolute"
      />
    );
  }

  alarmMessage() {
    const { formatMessage } = this.props.intl;

    const alarmTypes = [
      'Rain',
      'Snow',
      'Ice',
      'Sustained Wind',
      'Wind Gust',
      'Thunderstorm Probability'
    ];

    return (
      <div className="alarm-message">
        <FormattedMessage id={this.getI18nID('alarm.message', false, true)} />
        <ul className="alarm-list">
          {alarmTypes.map(alarmType => (
            <li key={`li-alarm-${alarmType.replace(' ', '-')}`}>
              {formatMessage({
                id: this.getI18nID(`alarmTypes.${alarmType}`, false, true)
              })}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  indicesOptions() {
    const indexTypes = [
      '-15',
      '-14',
      '-13',
      '-12',
      '-11',
      '-10',
      '-3',
      '-2',
      '1',
      '2',
      '3',
      '4',
      '5',
      '6',
      '7',
      '8',
      '9',
      '10',
      '11',
      '12',
      '13',
      '14',
      '15',
      '16',
      '17',
      '18',
      '19',
      '20',
      '21',
      '23',
      '24',
      '25',
      '26',
      '27',
      '28',
      '29',
      '30',
      '31',
      '32',
      '33',
      '34',
      '35',
      '36',
      '37',
      '38',
      '39',
      '40',
      '41',
      '42',
      '43',
      '44'
    ];

    return indexTypes
      .map(this.optionForIndices.bind(this))
      .sort(sortByField('label', this.props.intl.locale));
  }

  optionForIndices(index) {
    const { formatMessage } = this.props.intl;

    return {
      id: index,
      label: formatMessage({
        id: this.getI18nID(`indexTypes.${index}`, false, true)
      }),
      value: index
    };
  }

  selectedIndicesOption() {
    if (this.attribute().data_type) {
      return this.indicesOptions().filter(
        o => o.id === this.attribute().data_type
      );
    } else {
      return [];
    }
  }

  indicesComboboxFilter(inputValue, options, selection) {
    const inputValueRegExp = new RegExp(escapeRegExp(inputValue), 'ig');

    return options.filter(option => {
      const searchTermFoundInLabel = option.label
        ? option.label.match(inputValueRegExp)
        : false;
      const isSeparator = option.type === 'separator';
      const notAlreadySelected = option.id !== selection;

      return (
        (!inputValue || isSeparator || searchTermFoundInLabel) &&
        notAlreadySelected
      );
    });
  }

  indicesCombobox() {
    const { formatMessage } = this.props.intl;

    return (
      <div className="indices-combobox">
        <Combobox
          id="indices-combobox"
          variant="inline-listbox"
          labels={{
            label: formatMessage({
              id: this.getI18nID('index.title', false, true)
            })
          }}
          fieldLevelHelpTooltip={this.renderTooltip('index')}
          selection={this.selectedIndicesOption()}
          value={this.state.inputValue || ''}
          menuPosition="relative"
          options={this.indicesComboboxFilter(
            this.state.inputValue,
            this.indicesOptions(),
            this.attribute().data_type
          )}
          errorText={
            this.selectedIndicesOption().length
              ? null
              : formatMessage({ id: this.getI18nID('required') })
          }
          events={{
            onChange: (event, { value }) => {
              this.setState({ inputValue: value });
            },
            onRequestRemoveSelectedOption: (event, data) => {
              const attribute = {
                ...this.attribute(),
                data_type: [...this.attribute().data_type]
              };

              attribute.data_type = null;
              attribute.values = [];

              this.setState({ inputValue: '' });
              this.props.actions.updateModal(this.modalName, { attribute });
            },
            onSelect: (event, data) => {
              const { selection } = data;
              const attribute = { ...this.attribute() };

              attribute.data_type = selection.length
                ? selection[0].value
                : null;
              attribute.values = null;

              this.setState({ inputValue: '' });
              this.props.actions.updateModal(this.modalName, { attribute });
            }
          }}
        />
      </div>
    );
  }

  renderFooter() {
    const { formatMessage } = this.props.intl;

    /**
     * Cancel
     */
    const cancelLabel = formatMessage({ id: 'common.actions.cancel' });
    const cancelButton = (
      <Button
        label={cancelLabel}
        variant="neutral"
        onClick={this.closeModal.bind(this)}
      />
    );

    /**
     * Save
     */
    const saveID = `${this.mode()}-weather-attribute`;
    const saveLabel = formatMessage({ id: 'common.actions.save' });
    const saveButton = (
      <Button
        id={saveID}
        label={saveLabel}
        disabled={!this.canSave()}
        variant="brand"
        onClick={() => this.props.onSave(this.attribute())}
      />
    );

    return (
      <div className="footer-button-actions">
        {cancelButton}
        {saveButton}
      </div>
    );
  }

  dirty() {
    const current = this.attribute();
    const original = this.originalAttribute();
    const fields = ['values', 'details'];

    return fields.some(
      key => JSON.stringify(current[key]) !== JSON.stringify(original[key])
    );
  }

  canSave() {
    const attribute = this.attribute();

    let hasValues;
    switch (attribute.type) {
      case 'WeatherTemperatureAttribute':
        hasValues =
          this.noValuesWarning() && trimAndStrip(attribute.values).length;
        break;
      case 'WeatherAlarmAttribute':
        // alarm values will be added on the API side
        hasValues = true;
        break;
      case 'WeatherPrecipitationAttribute':
        hasValues =
          this.noValuesWarning() && trimAndStrip(attribute.values).length;
        break;
      case 'WeatherIndexAttribute':
        hasValues = attribute.data_type !== null;
        break;
      default:
        hasValues = false;
    }
    // edit mode
    if (attribute.uuid) {
      return this.dirty() && hasValues;
    } else {
      return attribute.name?.length && hasValues;
    }
  }

  attribute() {
    return this.props.modals[this.modalName]?.data?.attribute;
  }

  originalAttribute() {
    return this.props.modals[this.modalName]?.data?.original;
  }

  values() {
    return this.attribute().values;
  }

  forecast_ranges() {
    return this.attribute().details.forecast_ranges;
  }

  mode() {
    return this.attribute()?.uuid ? 'edit' : 'create';
  }

  getI18nID(suffix, modeBased, attributeBased) {
    const mode = modeBased ? this.mode() + '.' : '';
    const attribute = attributeBased ? this.props.attributeType : 'common';
    return `components.ecs.attributes.modal.${attribute}.${mode}${suffix}`;
  }

  closeModal() {
    this.setState({ activeWeatherType: null });
    this.props.actions.updateModal(this.modalName, {});
    this.props.actions.closeModal(this.modalName);
  }

  render() {
    const { formatMessage } = this.props.intl;
    const isOpen = this.props.modals[this.modalName]?.isOpen;
    const modalTitle = formatMessage({
      id: this.getI18nID('title', true, true)
    });

    if (!isOpen) {
      return null;
    }

    return (
      <Modal
        containerClassName="weather-attribute"
        heading={modalTitle}
        footer={this.renderFooter()}
        onRequestClose={this.closeModal.bind(this)}
        dismissOnClickOutside={false}
        disableClose={true}
        isOpen={isOpen || false}
      >
        {this.renderModal()}
      </Modal>
    );
  }
}

WeatherAttributeEditModal.propTypes = {
  actions: PropTypes.object.isRequired,
  flags: PropTypes.object,
  modals: PropTypes.object.isRequired,
  rules: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  attributeType: PropTypes.oneOf(['weather']).isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(WeatherAttributeEditModal);
